<template>
    <div class="form-group" :class="{ 'has-error': errorMessage }">
        <label v-if="label" class="control-label">{{ label }}</label>
        <select
            :id="inputId"
            v-model="selectedUserId"
            class="form-control"
            :name="name"
            :disabled="disabled"
            @change="emitChange"
        >
            <option v-if="allowEmpty" value="">{{ emptyLabel }}</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
                {{ user.label }}
            </option>
        </select>
        <p v-if="loadError" class="help-block text-warning">{{ loadError }}</p>
        <p v-if="errorMessage" class="help-block">{{ errorMessage }}</p>
    </div>
</template>

<script>
export default {
    props: {
        modelValue: {
            type: [Number, String, null],
            default: null,
        },
        accountId: {
            type: Number,
            required: true,
        },
        usersUrl: {
            type: String,
            default: '',
        },
        prefetchedUsers: {
            type: Array,
            default: () => [],
        },
        name: {
            type: String,
            default: 'owner_user_id',
        },
        label: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        errorMessage: {
            type: String,
            default: '',
        },
        inputId: {
            type: String,
            default: 'tenant-user-select',
        },
        allowEmpty: {
            type: Boolean,
            default: false,
        },
        emptyLabel: {
            type: String,
            default: '',
        },
        fallbackLabel: {
            type: String,
            default: '',
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            users: this.cloneUsers(this.prefetchedUsers),
            selectedUserId: this.normalizeSelectedValue(this.modelValue),
            loadError: '',
            loadedAccountId: null,
        };
    },
    watch: {
        accountId: {
            immediate: false,
            async handler(newAccountId) {
                await this.loadUsers(newAccountId);
            },
        },
        prefetchedUsers: {
            deep: true,
            handler(users) {
                if (this.loadedAccountId === null && users.length > 0) {
                    this.users = this.cloneUsers(users);
                    this.ensureSelectedUserListed();
                    this.syncSelectedValue();
                }
            },
        },
        modelValue(value) {
            this.selectedUserId = this.normalizeSelectedValue(value);
            this.ensureSelectedUserListed();
        },
    },
    async mounted() {
        this.ensureSelectedUserListed();
        this.syncSelectedValue();
        await this.loadUsers(this.accountId);
    },
    methods: {
        cloneUsers(users) {
            return users.map((user) => ({
                id: Number(user.id),
                label: String(user.label),
            }));
        },
        normalizeSelectedValue(value) {
            if (value === null || value === undefined || value === '') {
                return '';
            }

            return Number(value);
        },
        syncSelectedValue() {
            const normalized = this.normalizeSelectedValue(this.modelValue);

            if (this.selectedUserId !== normalized) {
                this.selectedUserId = normalized;
            }
        },
        async loadUsers(accountId = this.accountId) {
            this.loadError = '';

            if (accountId <= 0) {
                this.users = this.buildFallbackUsers();

                return;
            }

            if (
                this.loadedAccountId === accountId
                && this.users.length > 0
                && this.prefetchedUsers.length > 0
                && accountId === this.accountId
            ) {
                return;
            }

            if (! this.usersUrl) {
                this.users = this.prefetchedUsers.length > 0
                    ? this.cloneUsers(this.prefetchedUsers)
                    : this.buildFallbackUsers();
                this.ensureSelectedUserListed();

                return;
            }

            const url = `${this.usersUrl}?account_id=${encodeURIComponent(accountId)}`;

            try {
                const response = await fetch(url, {
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                if (! response.ok) {
                    if (this.users.length === 0 && this.prefetchedUsers.length > 0) {
                        this.users = this.cloneUsers(this.prefetchedUsers);
                    }

                    if (this.users.length === 0) {
                        this.users = this.buildFallbackUsers();
                    }

                    this.ensureSelectedUserListed();
                    this.loadError = 'Could not refresh tenant users.';

                    return;
                }

                const payload = await response.json();
                this.users = this.cloneUsers(payload.users || []);
                this.loadedAccountId = accountId;
                this.ensureSelectedUserListed();

                if (
                    ! this.allowEmpty
                    && (this.selectedUserId === '' || this.selectedUserId === null)
                    && this.users.length > 0
                ) {
                    this.selectedUserId = this.users[0].id;
                    this.emitChange();
                }
            } catch (error) {
                if (this.users.length === 0 && this.prefetchedUsers.length > 0) {
                    this.users = this.cloneUsers(this.prefetchedUsers);
                }

                if (this.users.length === 0) {
                    this.users = this.buildFallbackUsers();
                }

                this.ensureSelectedUserListed();
                this.loadError = 'Could not refresh tenant users.';
            }
        },
        buildFallbackUsers() {
            if (this.modelValue === null || this.modelValue === '') {
                return [];
            }

            return [{
                id: Number(this.modelValue),
                label: this.fallbackLabel || String(this.modelValue),
            }];
        },
        ensureSelectedUserListed() {
            if (this.modelValue === null || this.modelValue === '') {
                return;
            }

            const selectedId = Number(this.modelValue);

            if (! this.users.some((user) => user.id === selectedId)) {
                this.users = [
                    ...this.users,
                    {
                        id: selectedId,
                        label: this.fallbackLabel || String(this.modelValue),
                    },
                ];
            }
        },
        emitChange() {
            const value = this.selectedUserId === '' ? null : Number(this.selectedUserId);
            this.$emit('update:modelValue', value);
        },
    },
};
</script>
